/**
 * Video Optimizer - Lazy Loading for Video Iframes
 * 
 * This script handles lazy loading of video iframes to improve page performance.
 * Videos are only loaded when the user clicks the play button.
 */

// Prevent duplicate class declaration if script is loaded multiple times
if (typeof VideoOptimizer === 'undefined') {
  class VideoOptimizer {
    constructor() {
        this.init();
    }

    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.setupVideoFacades();
                this.setupResponsiveImages();
            });
        } else {
            this.setupVideoFacades();
            this.setupResponsiveImages();
        }
    }

    setupVideoFacades() {
        const videoFacades = document.querySelectorAll('.video-facade');
        
        videoFacades.forEach(facade => {
            // Skip if already initialized
            if (facade.dataset.initialized === 'true') {
                return;
            }

            facade.addEventListener('click', (e) => {
                e.preventDefault();
                this.loadVideo(facade);
            });

            // Add keyboard support for accessibility
            facade.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.loadVideo(facade);
                }
            });

            // Make facade focusable for keyboard navigation
            facade.setAttribute('tabindex', '0');
            facade.setAttribute('role', 'button');
            facade.setAttribute('aria-label', 'Play video');
            
            facade.dataset.initialized = 'true';
        });
    }

    setupResponsiveImages() {
        const videoThumbnails = document.querySelectorAll('.video-thumbnail[data-mobile-src][data-desktop-src]');
        
        // Function to update image source based on screen size
        const updateImageSource = () => {
            const isMobile = window.innerWidth <= 768;
            
            videoThumbnails.forEach(img => {
                const mobileSrc = img.dataset.mobileSrc;
                const desktopSrc = img.dataset.desktopSrc;
                
                if (isMobile && img.src !== mobileSrc) {
                    img.src = mobileSrc;
                } else if (!isMobile && img.src !== desktopSrc) {
                    img.src = desktopSrc;
                }
            });
        };

        // Set initial image source
        updateImageSource();

        // Listen for window resize events with debouncing
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(updateImageSource, 150);
        });

        // Listen for orientation change on mobile devices
        window.addEventListener('orientationchange', () => {
            setTimeout(updateImageSource, 100);
        });
    }

    loadVideo(facade) {
        const embedUrl = facade.dataset.embedUrl;
        const videoId = facade.dataset.videoId;

        const container = facade.closest('.two_media_column_video, .product_media_two_columns_video, .glaciotex_mod_video');
        const iframeContainer = container ? container.querySelector('.video-iframe-container') : null;
        const iframe = iframeContainer ? iframeContainer.querySelector('iframe') : null;

        if (!embedUrl || !iframe) {

            return;
        }

        // Show loading state
        this.showLoadingState(container);

        // Preload the iframe for faster loading
        iframe.loading = 'eager';
        
        // Add autoplay parameter dynamically (like your working example)
        const autoplayUrl = embedUrl + '&autoplay=1';
        iframe.src = autoplayUrl;

        // Handle iframe load event
        iframe.addEventListener('load', () => {
            this.hideLoadingState(container);
            this.showVideo(container, facade);
        }, { once: true });

        // Handle iframe error
        iframe.addEventListener('error', () => {
            this.hideLoadingState(container);
            this.showErrorState(container);
        }, { once: true });

        // Fallback timeout
        setTimeout(() => {
            if (iframe.src && (!iframeContainer.style.display || iframeContainer.style.display === 'none')) {
                this.hideLoadingState(container);
                this.showVideo(container, facade);
            }
        }, 3000);
    }

    showLoadingState(container) {
        // Remove existing loading placeholder if any
        const existingPlaceholder = container.querySelector('.video-loading-placeholder');
        if (existingPlaceholder) {
            existingPlaceholder.remove();
        }

        // Create loading placeholder
        const loadingPlaceholder = document.createElement('div');
        loadingPlaceholder.className = 'video-loading-placeholder';
        loadingPlaceholder.innerHTML = `
            <div class="loading-spinner"></div>
            <p>Loading video...</p>
        `;

        container.appendChild(loadingPlaceholder);
    }

    hideLoadingState(container) {
        const loadingPlaceholder = container.querySelector('.video-loading-placeholder');
        if (loadingPlaceholder) {
            loadingPlaceholder.remove();
        }
    }

    showVideo(container, facade) {
        const iframeContainer = container.querySelector('.video-iframe-container');
        
        if (iframeContainer) {
            // Hide the facade
            facade.style.display = 'none';
            
            // Show the iframe
            iframeContainer.style.display = 'block';
            
            // Add a class to indicate video is loaded
            container.classList.add('video-loaded');
        }
    }

    showErrorState(container) {
        const errorPlaceholder = document.createElement('div');
        errorPlaceholder.className = 'video-loading-placeholder';
        errorPlaceholder.innerHTML = `
            <div style="font-size: 24px; margin-bottom: 10px;">⚠️</div>
            <p>Failed to load video</p>
        `;

        container.appendChild(errorPlaceholder);
    }

  }

  // Make VideoOptimizer available globally
  window.VideoOptimizer = VideoOptimizer;
}

// Initialize the video optimizer only once
if (!window.videoOptimizerInitialized) {
  window.videoOptimizerInitialized = true;
  new VideoOptimizer();
}

// Re-initialize when new content is added dynamically (for theme editors)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                // Check if any new video facades were added
                const hasNewFacades = Array.from(mutation.addedNodes).some(node => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        return node.querySelector && node.querySelector('.video-facade');
                    }
                    return false;
                });

                if (hasNewFacades && window.VideoOptimizer) {
                    // Re-initialize after a short delay to ensure DOM is ready
                    setTimeout(() => {
                        const optimizer = new window.VideoOptimizer();
                        // Setup new facades without re-initializing everything
                        optimizer.setupVideoFacades();
                    }, 100);
                }
            }
        });
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
}
